import { useEffect, useState } from 'react';
import { Spinner } from '../../components/spinner/spinner';
import { Typography } from '../../components/typography/typography';
import './accounting.scss';
import {
  GeneralLedgerAccount,
  GeneralLedgerAccountType,
  ItemGeneralLedgerAccount,
  ItemGeneralLedgerTransactionType,
  ItemType,
} from '../../clients/quipli-service/accounting/accounting';
import { useAccountingApiV1 } from '../../clients/quipli-service/accounting/accounting-api-v1';
import { ConfirmModal } from '../../components/modal/confirm-modal';
import { SelectForm } from '../../components/select-form/select-form';

function filterAccounts(
  accounts: GeneralLedgerAccount[],
  transactionType?: ItemGeneralLedgerTransactionType,
): GeneralLedgerAccount[] {
  switch (transactionType) {
    case ItemGeneralLedgerTransactionType.Rental:
    case ItemGeneralLedgerTransactionType.Sale:
      return accounts.filter((acc) => acc.accountType === GeneralLedgerAccountType.Income);
    default:
      return accounts.filter((acc) =>
        [GeneralLedgerAccountType.Income, GeneralLedgerAccountType.Expense].includes(acc.accountType),
      );
  }
}

export const ItemGeneralLedgerAccountInput = ({
  itemType,
  itemId,
  transactionType,
  label,
  accounts: defaultAccounts,
}: {
  itemType?: ItemType;
  itemId?: string;
  transactionType?: ItemGeneralLedgerTransactionType;
  label?: string;
  accounts?: GeneralLedgerAccount[];
}) => {
  const [accounts, setAccounts] = useState<GeneralLedgerAccount[] | undefined>(
    defaultAccounts && filterAccounts(defaultAccounts, transactionType),
  );
  const [itemGeneralLedgerAccount, setItemGeneralLedgerAccount] = useState<ItemGeneralLedgerAccount>();
  const { listAccounts, getItemGeneralLedgerAccount, upsertItemGeneralLedgerAccount } = useAccountingApiV1();
  const [pendingAccountReassignment, setPendingAccountReassignment] = useState<string | undefined>(undefined);

  useEffect(() => {
    const accounts = defaultAccounts;
    if (!accounts) {
      listAccounts().then((accounts) => {
        setAccounts(filterAccounts(accounts, transactionType));
      });
    }
    getItemGeneralLedgerAccount(itemType, itemId, transactionType).then(setGeneralLedgerAccount);
  }, []);

  function setGeneralLedgerAccount(ia: ItemGeneralLedgerAccount | undefined) {
    if (
      ia &&
      (ia.itemType || undefined) === itemType &&
      (ia.itemId || undefined) === itemId &&
      (ia.transactionType || undefined) === transactionType
    ) {
      setItemGeneralLedgerAccount(ia);
    } else {
      setItemGeneralLedgerAccount(undefined);
    }
  }

  function submit(generalLedgerAccountId?: string, reassignExistingItems?: boolean) {
    setPendingAccountReassignment(undefined);
    upsertItemGeneralLedgerAccount({
      itemType,
      itemId,
      transactionType,
      generalLedgerAccountId,
      reassignExistingItems,
    }).then(setGeneralLedgerAccount);
  }

  return (
    <div className="item-income-account container-v2">
      {accounts ? (
        <div className="flex flex-row">
          {label && (
            <label>
              <Typography type="text-md" weight="semi-bold">
                {label || 'General Ledger Account'}:
              </Typography>
            </label>
          )}
          <SelectForm
            value={{
              value: itemGeneralLedgerAccount?.generalLedgerAccountId,
              label:
                accounts.find((acc) => acc.id === itemGeneralLedgerAccount?.generalLedgerAccountId)?.name ??
                'Select GL Account',
            }}
            options={accounts.map((gl) => ({ label: gl.name, value: gl.id }))}
            onChange={(v) =>
              v && itemType && itemType !== ItemType.CustomProduct
                ? setPendingAccountReassignment(v?.value)
                : submit(v?.value, false)
            }
            isClearable={true}
            className="select-account"
            menuPlacement="top"
          />
          {pendingAccountReassignment && (
            <ConfirmModal
              isOpen={true}
              close={() => submit(pendingAccountReassignment, false)}
              onConfirm={() => submit(pendingAccountReassignment, true)}
              prompt={'Would you like to reassign historical data?'}
            />
          )}
        </div>
      ) : (
        <Spinner />
      )}
    </div>
  );
};
